/**
 * What a product costs, from the supplier it is being bought from.
 *
 * The catalogue holds one name for a thing — a 13" ridge cap is a 13" ridge cap
 * wherever it is bought — and that is right. What is not one number is the
 * price. Two suppliers quote the same trim differently, and until now whichever
 * price sheet was loaded last won: Central States' book overwrote Heely-Brown's
 * in the same column, and the next sheet would have overwritten that.
 *
 * So a price belongs to a list and a supplier reads off a list. Each supplier
 * has its own: Heely-Brown and ABC Supply shared one until ABC's invoice showed
 * they do not quote alike (Jonathon, 2026-10-02: "each supplier needs it's own
 * pricing", ADR 0257). `default_cost` is still the answer when nobody has a
 * better one, which is what it always meant.
 *
 * Nothing here reprices an order that already exists. A line carries the price
 * it was written at, because that is what the supplier agreed to.
 */

import { toCents, type Cents } from "./money.js";

/** A row of `product_prices`. */
export interface ProductPrice {
  price_list_id: string;
  product_id: string;
  cost: unknown;
}

/** A supplier, as far as pricing is concerned. */
export interface PricedSupplier {
  id: string;
  price_list_id?: string | null;
}

/**
 * A lookup of price-list costs, ready to ask.
 *
 * Built once per generation rather than per line — a template of forty products
 * priced against a catalogue of six hundred should not be forty scans.
 */
export class PriceBook {
  private readonly byListAndProduct = new Map<string, number>();
  private readonly listBySupplier = new Map<string, string>();

  constructor(
    prices: readonly ProductPrice[] = [],
    suppliers: readonly PricedSupplier[] = [],
  ) {
    for (const row of prices) {
      const cost = Number(row.cost);
      if (!Number.isFinite(cost)) continue;
      this.byListAndProduct.set(`${row.price_list_id}:${row.product_id}`, cost);
    }
    for (const supplier of suppliers) {
      if (supplier.price_list_id) {
        this.listBySupplier.set(supplier.id, supplier.price_list_id);
      }
    }
  }

  /** The price list a supplier reads off, or null if it has none. */
  listFor(supplierId: string | null | undefined): string | null {
    if (!supplierId) return null;
    return this.listBySupplier.get(supplierId) ?? null;
  }

  /**
   * What one product costs from one supplier, in cents.
   *
   * Falls through to the product's own cost when the supplier has no list, or
   * has one that does not carry this product. A supplier we have no sheet for
   * is not a supplier whose prices are zero.
   */
  costOf(
    product: { id: string; default_cost: unknown },
    supplierId: string | null | undefined,
  ): Cents {
    const listed = this.listedCost(product.id, supplierId);
    return toCents(listed ?? product.default_cost);
  }

  /**
   * The list price alone, or null if this supplier does not price this product.
   *
   * Kept separate from `costOf` so a screen can say "this is the supplier's own
   * price" rather than showing a fallback as though it were quoted.
   */
  listedCost(
    productId: string,
    supplierId: string | null | undefined,
  ): number | null {
    const list = this.listFor(supplierId);
    if (!list) return null;
    return this.byListAndProduct.get(`${list}:${productId}`) ?? null;
  }

  /** How many prices are held, for a screen that wants to say so. */
  get size(): number {
    return this.byListAndProduct.size;
  }
}

/** An empty book — every product falls back to its own cost. */
export const NO_PRICES = new PriceBook();
